import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { MapPathStyle } from '../canvas/types';
import { sliderValue } from '../canvas/slider-utils';

const PRESET_COLORS = [
    '#8B7355', '#4a90d9', '#e67e22', '#9b59b6',
    '#2ecc71', '#e74c3c', '#f39c12', '#95a5a6',
];

function ColorSwatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                width: 20, height: 20, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                border: selected ? '2px solid white' : '2px solid transparent',
                boxShadow: selected ? `0 0 0 2px ${color}` : 'none',
                transition: 'all 0.15s',
                flexShrink: 0,
            }}
        />
    );
}

interface PathStyleEditorProps {
    style: MapPathStyle;
    onChange: (partial: Partial<MapPathStyle>) => void;
}

export function PathStyleEditor({ style, onChange }: PathStyleEditorProps) {
    const { t } = useTranslation();

    return (
        <Stack spacing={1.5} mt={1} pl={0.5} onClick={e => e.stopPropagation()}>
            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.paths.style.color')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                    {PRESET_COLORS.map(c => (
                        <ColorSwatch key={c} color={c} selected={style.strokeColor === c} onClick={() => onChange({ strokeColor: c })} />
                    ))}
                    <Box
                        component="input"
                        type="color"
                        value={style.strokeColor}
                        onChange={e => onChange({ strokeColor: e.target.value })}
                        title={t('map_creator.paths.style.custom_color')}
                        sx={{ width: 20, height: 20, border: 'none', borderRadius: '50%', p: 0, cursor: 'pointer', bgcolor: 'transparent' }}
                    />
                </Stack>
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.paths.style.width', { value: style.strokeWidth })}
                </Typography>
                <Slider
                    size="small"
                    min={0.5} max={12} step={0.5}
                    value={style.strokeWidth}
                    onChange={(_, v) => onChange({ strokeWidth: sliderValue(v) })}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.paths.style.opacity', { value: Math.round(style.opacity * 100) })}
                </Typography>
                <Slider
                    size="small"
                    min={0} max={1} step={0.05}
                    value={style.opacity}
                    onChange={(_, v) => onChange({ opacity: sliderValue(v) })}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.paths.style.line_style')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5}>
                    {([
                        { label: t('map_creator.paths.style.solid'), dashed: false },
                        { label: t('map_creator.paths.style.dashed'), dashed: true },
                    ] as const).map(opt => (
                        <Chip
                            key={String(opt.dashed)}
                            label={opt.label}
                            size="small"
                            onClick={() => onChange({ dashed: opt.dashed })}
                            variant={style.dashed === opt.dashed ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.65rem' }}
                        />
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}
